import React, { useEffect, useState, useRef, useCallback } from 'react';
import api from '../../services/apiClient.ts';
import { RefreshCw, Calendar, FileText, Search, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import DateInput from '../common/DateInput';

const API_BASE = '/v1';
const API_URL = `${API_BASE}/reports/traveler-expenses`;
const SEARCH_DEBOUNCE_MS = 350;

const headersAuth = {
  'X-Tenant': localStorage.getItem('tenant') || 'default-tenant-1',
  Accept: 'application/json',
};

function formatMoney(val) {
  const num = Number(val ?? 0);
  return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debouncedValue;
}

function SearchableSelect({
  label,
  value,
  displayLabel,
  onSelect,
  options,
  onSearch,
  searchLoading,
  placeholder = 'الكل',
  clearOptionLabel,
  getOptionLabel,
  getOptionValue,
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debouncedQuery = useDebounce(query, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    if (!open) return;
    onSearch(debouncedQuery);
  }, [open, debouncedQuery, onSearch]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectOption = useCallback((opt, isClear) => {
    if (isClear) {
      onSelect('', '');
      setOpen(false);
      return;
    }
    const id = getOptionValue(opt);
    const name = getOptionLabel(opt);
    onSelect(id, name);
    setOpen(false);
  }, [getOptionValue, getOptionLabel, onSelect]);

  const baseInputClass = 'w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 text-right';

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{label}</label>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`${baseInputClass} flex items-center justify-between gap-2 min-h-[42px]`}
      >
        <span className="truncate">{displayLabel || placeholder}</span>
        <ChevronDown className={`w-4 h-4 shrink-0 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-[100] top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-xl overflow-hidden">
          <div className="p-2 border-b border-gray-200 dark:border-gray-600">
            <div className="relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="بحث..."
                className="w-full py-2 pr-10 pl-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="max-h-56 overflow-y-auto">
            {searchLoading ? (
              <div className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">جاري البحث...</div>
            ) : (
              <>
                {clearOptionLabel && (
                  <button
                    type="button"
                    onClick={() => selectOption(null, true)}
                    className={`w-full px-4 py-2.5 text-right text-sm hover:bg-amber-50 dark:hover:bg-gray-700 transition-colors ${!value ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200' : 'text-gray-900 dark:text-gray-100'}`}
                  >
                    {clearOptionLabel}
                  </button>
                )}
                {options.length === 0 && !clearOptionLabel ? (
                  <div className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">لا توجد نتائج</div>
                ) : (
                  options.map((opt) => (
                    <button
                      key={getOptionValue(opt)}
                      type="button"
                      onClick={() => selectOption(opt, false)}
                      className={`w-full px-4 py-2.5 text-right text-sm hover:bg-amber-50 dark:hover:bg-gray-700 transition-colors ${value === String(getOptionValue(opt)) ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200' : 'text-gray-900 dark:text-gray-100'}`}
                    >
                      {getOptionLabel(opt)}
                    </button>
                  ))
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const TravelerExpensesReport = () => {
  const { t } = useTranslation?.() ?? { t: (k) => k };
  const [grId, setGrId] = useState('');
  const [grLabel, setGrLabel] = useState('');
  const [trId, setTrId] = useState('');
  const [trLabel, setTrLabel] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [groupOptions, setGroupOptions] = useState([]);
  const [travelerOptions, setTravelerOptions] = useState([]);
  const [groupSearchLoading, setGroupSearchLoading] = useState(false);
  const [travelerSearchLoading, setTravelerSearchLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const branchId = localStorage.getItem('branch_id') || localStorage.getItem('branch') || '1';

  const fetchGroups = useCallback(async (q) => {
    setGroupSearchLoading(true);
    try {
      const resp = await api.get(`${API_BASE}/groups`, {
        params: { q: q || '', per_page: 30 },
        headers: headersAuth,
      });
      const gData = resp?.data?.data ?? resp?.data ?? [];
      setGroupOptions(Array.isArray(gData) ? gData : (gData?.data ? gData.data : []));
    } catch {
      setGroupOptions([]);
    } finally {
      setGroupSearchLoading(false);
    }
  }, []);

  const fetchTravelers = useCallback(async (q) => {
    setTravelerSearchLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (grId) params.gr_id = grId;

      const resp = await api.get(`${API_BASE}/reports/travelers-list`, {
        params,
        headers: { ...headersAuth, 'X-Branch': String(branchId) },
      });
      const tData = resp?.data?.data ?? resp?.data ?? [];
      setTravelerOptions(Array.isArray(tData) ? tData : []);
    } catch {
      setTravelerOptions([]);
    } finally {
      setTravelerSearchLoading(false);
    }
  }, [branchId, grId]);

  useEffect(() => {
    // When group changes, refresh combobox2 options to linked passengers only
    fetchTravelers('');
  }, [grId, fetchTravelers]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (grId) params.gr_id = grId;
      if (trId) params.tr_id = trId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const resp = await api.get(API_URL, { params });
      const data = resp?.data?.data ?? resp?.data ?? [];
      setRows(Array.isArray(data) ? data : []);
      setTotal(Number(resp?.data?.total ?? 0));
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Network Error');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="text-gray-900 dark:text-gray-100 py-6 px-4 sm:px-6 lg:px-8">
      <div className="w-full mx-auto">
        {/* Header Card - بدون overflow-hidden حتى تظهر القوائم المنسدلة */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 mb-8">
          <div className="overflow-hidden rounded-t-2xl bg-gradient-to-r from-amber-500 to-orange-600 px-8 py-6">
            <div className="flex items-center space-x-reverse space-x-3">
              <div className="bg-white/20 rounded-full p-3">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">تقرير صرفيات المسافرين</h2>
                <p className="text-amber-100 mt-1">عرض وتصفية مصاريف المسافرين حسب المجموعة والمسافر والتاريخ</p>
              </div>
            </div>
          </div>

          {/* فلاتر - overflow-visible حتى تظهر القوائم المنسدلة خارج الكارت */}
          <div className="p-8 overflow-visible">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 items-end overflow-visible">
              <SearchableSelect
                label="اسم المجموعة"
                value={grId}
                displayLabel={grLabel}
                onSelect={(id, label) => {
                  setGrId(id);
                  setGrLabel(label || '');
                  setTrId('');
                  setTrLabel('');
                }}
                options={groupOptions}
                onSearch={fetchGroups}
                searchLoading={groupSearchLoading}
                placeholder="الكل"
                clearOptionLabel="الكل"
                getOptionValue={(g) => g.id}
                getOptionLabel={(g) => g.gr_notes || `المجموعة ${g.id}`}
              />
              <SearchableSelect
                label="اسم المسافر"
                value={trId}
                displayLabel={trLabel}
                onSelect={(id, label) => { setTrId(id); setTrLabel(label || ''); }}
                options={travelerOptions}
                onSearch={fetchTravelers}
                searchLoading={travelerSearchLoading}
                placeholder="الكل"
                clearOptionLabel="الكل"
                getOptionValue={(t) => t.id}
                getOptionLabel={(t) => t.name || `مسافر ${t.id}`}
              />
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">من تاريخ</label>
                <div className="relative">
                  <DateInput

 value={dateFrom}
 onChange={(e) => setDateFrom(e.target.value)}
 className="w-full pl-10 rtl:pl-3 rtl:pr-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200"
 />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">إلى تاريخ</label>
                <div className="relative">
                  <DateInput

 value={dateTo}
 onChange={(e) => setDateTo(e.target.value)}
 className="w-full pl-10 rtl:pl-3 rtl:pr-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200"
 />
                </div>
              </div>
              <div className="flex sm:justify-end">
                <button
                  onClick={fetchData}
                  disabled={loading}
                  className="inline-flex items-center justify-center w-full sm:w-auto px-5 py-3 rounded-xl bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400 text-white font-medium shadow-sm transition-colors duration-200"
                  title="تحديث"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2 ${loading ? 'animate-spin' : ''}`} />
                  {loading ? 'جاري الجلب...' : 'تحديث'}
                </button>
              </div>
            </div>
            {error && (
              <div className="mt-4 text-sm text-red-600 dark:text-red-400">{error}</div>
            )}
          </div>
        </div>

        {/* الجدول */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700">
          <div className="app-table-wrap">
            <table className="app-table min-w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 border-b border-gray-200 dark:border-gray-600">
                <tr>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">اسم المجموعة</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">اسم المسافر</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">نوع المصروف</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">التاريخ</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">المبلغ</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">ملاحظات</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                      جاري الجلب...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                      لا توجد بيانات
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">{r.group_name ?? '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{r.traveler_name ?? '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{r.expense_type ?? '—'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        {r.expense_date ? String(r.expense_date).slice(0, 10) : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {formatMoney(r.amount)} د.ع
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate" title={r.notes ?? ''}>{r.notes ?? '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot className="bg-gray-50 dark:bg-gray-900 border-t-2 border-gray-200 dark:border-gray-700">
                <tr>
                  <td colSpan={4} className="px-6 py-4 text-right text-sm font-bold text-gray-700 dark:text-gray-200">
                    المجموع
                  </td>
                  <td className="px-6 py-4 text-right text-sm font-extrabold text-amber-700 dark:text-amber-400">
                    {formatMoney(total)} د.ع
                  </td>
                  <td className="px-6 py-4" />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TravelerExpensesReport;
